import type React from 'react';
import type { DeptColors } from '../../constants/sidebarConstants';
import { launchpadStrings } from '../../components/StringBundle';
/** State object for the Snackbar notification component. */
export interface SnackbarState {
  open: boolean;
  message: string;
  severity: string;
}

// Filters

/** A single active column-filter entry used by NestedTable / FilterUtils. */
export interface FilterSetting {
  searchedColumn: string;
  searchText: string;
}

//  Select  

/** Generic label/value pair used by dropdowns and filter selects. */
export interface SelectOption {
  label: string;
  value: string | number;
}

//  Pagination 

/** Pagination configuration object passed to NestedTable. */
export interface PaginationParams {
  page: number;
  pageLimit: number;
  totalCount: number;
  onPageChange: (page: number) => void;
  pageSizeOptions: number[];
  onPageSizeChange: (size: number) => void;
}

// ─── mapObjectToArray (screens/common.js, untyped JS) ─────────────────────────
export interface MapObjectToArrayColumnEntry {
  key: string;
  label: string;
  labelSm: string | number;
  valueSm: string | number;
  [key: string]: unknown;
}

// ─── API errors ────────────────────────────────────────────────────────────────
export interface ApiError {
  response?: {
    data?: string;
    [key: string]: unknown;
  };
  message?: string;
}


// ─── AppSidebar ───────────────────────────────────────────────────────────────

/** CSS properties extended to allow CSS custom property variables (e.g. --c, --tint). */
export type CSSPropertiesWithVars = React.CSSProperties & {
  [key: string]: string | number | undefined;
};

/** A single department entry rendered in the sidebar rail. */
export interface Department {
  key: string;
  name: string;
  Icon: React.ElementType;
  items: RouteItem[];
  /** Pre-computed accent and tint colours for this department. */
  colors: DeptColors;
}

/** Position state for the flyout panel (positioned via a React portal). */
export interface FlyoutPos {
  top: number;
  left: number;
  maxHeight?: number;
}

/** Public props accepted by the AppSidebar component. */
export interface AppSidebarProps {
  current_role?: string;
  collapsed?: boolean;
  setCollapsed?: React.Dispatch<React.SetStateAction<boolean>>;
  onUserClick?: () => void;
}

// ─── BreadcrumbBar ────────────────────────────────────────────────────────────

/** Minimum shape a breadcrumb item must satisfy for ContentBreadcrumbs. */
export interface ContentBreadcrumbItem {
  label: string;
  to?: string | null;
}

/** Props for the ContentBreadcrumbs presentational component. */
export interface ContentBreadcrumbsProps {
  items: ContentBreadcrumbItem[];
  onNavigate?: (item: ContentBreadcrumbItem) => void;
  sticky?: boolean;
}


export interface BreadcrumbCrumb {
  label: string;
  to: string | null;
  deptKey?: string | null;
  tab?: string | null;
  kind?: 'module' | 'page' | 'action';
}

export interface TrailCrumb {
  label: string;
  to: string;
  key: string;
  pagePath: string;
  deptKey?: string | null;
  kind?: string;
  title: string;
  path: string;
  params: { ids?: string[] };
  queryParams: Record<string, string>;
  tab: string | null;
  state: Record<string, unknown>;
  id: string;
}

export interface StoredCrumb {
  label:    string;
  to:       string;
  pagePath: string;
  deptKey?: string | null;
  kind?:    string;
  tab?:     string | null;
  state?:   Record<string, unknown>;
}

/** A step whose breadcrumb crumb links back to a different step ({label,
 *  step}) and is immediately followed by the current step's own,
 *  non-linking crumb (currentLabel) — e.g. "Calendar Blocking > Quick
 *  Preview", where "Calendar Blocking" links back to step=confirm. */
export interface StepCrumbEntry {
  label: string;
  step: string;
  currentLabel?: string;
}

export type StepLabelOverrides = Record<string, string | StepCrumbEntry>;

/** Breadcrumb action labels sourced from the string bundle. Export allows
 *  other files (e.g. BreadcrumbTrailContext) to compare against the same values. */
export const CRUMB_LABELS = launchpadStrings.breadcrumb as {
  navAriaLabel: string;
  actionCreate: string;
  actionView:   string;
  actionEdit:   string;
  actionDelete: string;
  actionAdd:    string;
};

export interface SidebarDeptDef {
  key: string;
  name: string;
  Icon: React.ElementType;
  routes: RouteItem[];
}

/** A single route entry as defined in routes.js */
export interface RouteItem {
  label: string;
  to: string;
  roles: string[];
}

/** A single module entry in the MODULE_CONFIG array */
export interface ModuleConfigEntry {
  moduleLabel: string;
  routes: RouteItem[];
}

/** Path → module/page label mapping built from MODULE_CONFIG in useBreadcrumbs. */
export interface RouteLookupEntry {
  moduleLabel: string;
  pageLabel: string;
}

/** Options accepted by createTypewriter */
export interface TypewriterOptions {
  texts: string[];
  typingSpeed?: number;
  deletingSpeed?: number;
  pauseDuration?: number;
  loop?: boolean;
  startIndex?: number;
  onChar: (text: string) => void;
  onDone?: () => void;
}

/** Handle returned by createTypewriter to allow cancellation */
export interface TypewriterHandle {
  stop: () => void;
}

